import React, { useCallback, useEffect, useRef, useState } from 'react';
import CommonBubble from '@home/components/CommonBubble';
import type { ILegendItemRect } from '../_config/interface';

type TooltipMap = Record<string, { en: string; cn: string }> | undefined;

interface IUseLegendTooltipOptions {
  tooltipMap: TooltipMap;
  isEN: boolean;
}

interface ILegendBubbleState {
  content: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * 为 ECharts legend 提供 hover 气泡说明。
 * 管理气泡状态与事件回调，返回可直接传给 Chart / StairsChart 的 props 和待渲染的气泡元素。
 */
export default function useLegendTooltip({ tooltipMap, isEN }: IUseLegendTooltipOptions) {
  const [legendBubble, setLegendBubble] = useState<ILegendBubbleState | null>(null);

  const tooltipMapRef = useRef(tooltipMap);
  useEffect(() => { tooltipMapRef.current = tooltipMap; }, [tooltipMap]);

  const handleLegendMouseover = useCallback((name: string, zrEvent: any, itemRect?: ILegendItemRect) => {
    const map = tooltipMapRef.current;
    const tooltip = isEN ? map?.[name]?.en : map?.[name]?.cn;
    if (!tooltip) return;

    if (itemRect) {
      setLegendBubble({ content: tooltip, left: itemRect.left, top: itemRect.top, width: itemRect.width, height: itemRect.height });
    } else {
      const nativeEvt = zrEvent?.event;
      const x = nativeEvt?.clientX;
      const y = nativeEvt?.clientY;
      if (x == null || y == null) return;
      setLegendBubble({ content: tooltip, left: x, top: y, width: 1, height: 1 });
    }
  }, [isEN]);

  const handleLegendMouseout = useCallback(() => {
    setLegendBubble(null);
  }, []);

  const onLegendMouseover = tooltipMap ? handleLegendMouseover : undefined;
  const onLegendMouseout = tooltipMap ? handleLegendMouseout : undefined;

  const LegendTooltipBubble = legendBubble ? (
    <CommonBubble
      visible
      trigger="empty"
      content={legendBubble.content}
      placement="bottom"
      dark
      arrowPointAtCenter
      positionFixed
      referenceElement={{
        getBoundingClientRect: () => ({
          top: legendBubble.top,
          left: legendBubble.left,
          right: legendBubble.left + legendBubble.width,
          bottom: legendBubble.top + legendBubble.height,
          width: legendBubble.width,
          height: legendBubble.height,
          x: legendBubble.left,
          y: legendBubble.top,
          toJSON() { return this; },
        }),
        clientWidth: legendBubble.width,
        clientHeight: legendBubble.height,
      } as any}
      updateDeps={[legendBubble.left, legendBubble.top, legendBubble.width, legendBubble.height, legendBubble.content]}
    >
      <span style={{ display: 'none' }} />
    </CommonBubble>
  ) : null;

  return { onLegendMouseover, onLegendMouseout, LegendTooltipBubble };
}
